import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useDispatch } from '@bonusly/events';
import { getHttpClient, useHttpClient } from '@bonusly/http-client';
import { Recipient, RecipientAPIData } from '@bonusly/recipient-entities';

import {
	GIVE_RECOGNITION_AMOUNT_QUERY_PARAM,
	GIVE_RECOGNITION_HASHTAGS_QUERY_PARAM,
	GIVE_RECOGNITION_MODAL_QUERY_PARAM,
	GIVE_RECOGNITION_RECIPIENTS_QUERY_PARAM,
	GIVE_RECOGNITION_TEXT_QUERY_PARAM,
} from '../constants';
import { GiveBoxEvent } from '../events';

// Module-level variable to ensure hook only runs once across component remounts
let hasInitializedGlobally = false;

/**
 * Upper bound on the assembled recognition body. Recognition messages are
 * short in practice (the give-box itself caps well under this), so a draft
 * longer than this almost certainly came from a malformed / overstuffed
 * deep-link. We don't crash on it — we pre-fill what we can and warn. See BIZY-422.
 */
const MAX_DEEP_LINK_TEXT_LENGTH = 5000;

/**
 * URL parameter value types for give recognition modal
 * - null: parameter not present
 * - 'true': open modal without pre-selection
 * - string: user ID or group token to pre-select
 */
type GiveRecognitionParam = string | null;

/**
 * Splits a comma-separated query param into trimmed, non-empty tokens.
 * Returns an empty array when the param is absent or blank.
 */
const parseCommaSeparatedParam = (rawParam: string | null): string[] => {
	if (!rawParam) return [];
	return rawParam
		.split(',')
		.map((token) => token.trim())
		.filter((token) => token.length > 0);
};

/**
 * URL-decode the body defensively. A handful of clients double-encode, and a
 * stray `%` that isn't a valid escape makes `decodeURIComponent` throw — in
 * that case we fall back to the raw string rather than blocking the modal.
 */
const decodeDeepLinkText = (rawText: string | null): string | undefined => {
	if (!rawText) return undefined;
	let decodedText: string;
	try {
		decodedText = decodeURIComponent(rawText);
	} catch {
		decodedText = rawText;
	}
	if (decodedText.length > MAX_DEEP_LINK_TEXT_LENGTH) {
		console.warn(`[give-recognition deep link] message body of ${decodedText.length} chars exceeds ${MAX_DEEP_LINK_TEXT_LENGTH}; truncating.`);
		return decodedText.slice(0, MAX_DEEP_LINK_TEXT_LENGTH);
	}
	return decodedText;
};

/**
 * Resolves a single username to a full `Recipient` via the v1
 * `/users/autocomplete` endpoint (response shape `{ data: { result: RecipientAPIData[] } }`,
 * each entry carrying the DB `id`, `username`, `display_name`, `profile_pic_url`).
 * Returns `undefined` when the username can't be resolved so a bad token in the
 * list doesn't block the other recipients or the modal from opening.
 */
const resolveRecipientByUsername = async (username: string): Promise<Recipient | undefined> => {
	try {
		const httpV1 = await getHttpClient('v1');
		const autocompleteResponse = await httpV1.get('/users/autocomplete', {
			params: { search: username },
		});

		const matchingRecipients = (autocompleteResponse.data?.result ?? []) as RecipientAPIData[];
		// Only accept a case-insensitive exact username match. Falling back to the
		// first autocomplete result could pre-select a different person than the
		// deep link named, so an unmatched username stays unresolved instead.
		const exactMatch = matchingRecipients.find((candidate) => candidate.username?.toLowerCase() === username.toLowerCase());

		return exactMatch ? new Recipient(exactMatch) : undefined;
	} catch (recipientLookupError) {
		console.warn(`[give-recognition deep link] could not resolve recipient "${username}":`, recipientLookupError);
		return undefined;
	}
};

/**
 * Hook that checks for URL parameters to automatically open the Give Recognition Modal
 * with optional user pre-selection and text. Uses the existing 'init-give-recognition' event
 * system that's already used throughout the codebase.
 *
 * Existing (unchanged) params:
 * - ?give_recognition=true - Opens modal without pre-selection
 * - ?give_recognition=68a5045a50720374d58d0de3 - Opens modal with user pre-selected by userId
 * - ?give_recognition=userId&give_recognition_text=Great job! - Opens modal with both user and text
 *
 * Rich Bizy Desktop hand-off params (external-browser deep link; additive, BIZY-422):
 * - ?give_recognition_recipients=alice,bob - comma-separated USERNAMES, each resolved to a full Recipient
 * - ?give_recognition_amount=10 - numeric points amount
 * - ?give_recognition_hashtags=teamwork,wins - comma-separated tags WITHOUT a leading '#'
 */
export const useInitGiveRecognitionModal = () => {
	const [params] = useSearchParams();
	const dispatch = useDispatch();
	const http = useHttpClient('v2');

	// Using useEffect to handle URL-based modal initialization
	useEffect(() => {
		const hasRichDeepLinkParams =
			params.has(GIVE_RECOGNITION_RECIPIENTS_QUERY_PARAM) ||
			params.has(GIVE_RECOGNITION_AMOUNT_QUERY_PARAM) ||
			params.has(GIVE_RECOGNITION_HASHTAGS_QUERY_PARAM);

		// Skip if already initialized or no relevant query params
		if (
			hasInitializedGlobally ||
			(!params.has(GIVE_RECOGNITION_MODAL_QUERY_PARAM) && !params.has(GIVE_RECOGNITION_TEXT_QUERY_PARAM) && !hasRichDeepLinkParams)
		) {
			return;
		}

		const initModal = async () => {
			hasInitializedGlobally = true;

			// Rich Bizy Desktop hand-off: resolve every username to a full
			// Recipient (so they survive submission), build the full payload,
			// and reuse the SAME GiveBoxEvent.Open event the in-app flow uses
			// so the editor pre-fills identically. See BIZY-422.
			if (hasRichDeepLinkParams) {
				await dispatchRichDeepLink();
				return;
			}

			await dispatchLegacyDeepLink();
		};

		// Builds and dispatches the rich, multi-recipient payload.
		const dispatchRichDeepLink = async () => {
			const usernames = parseCommaSeparatedParam(params.get(GIVE_RECOGNITION_RECIPIENTS_QUERY_PARAM));
			const messageText = decodeDeepLinkText(params.get(GIVE_RECOGNITION_TEXT_QUERY_PARAM));
			const hashtags = parseCommaSeparatedParam(params.get(GIVE_RECOGNITION_HASHTAGS_QUERY_PARAM));

			const rawAmount = params.get(GIVE_RECOGNITION_AMOUNT_QUERY_PARAM);
			const parsedAmount = rawAmount != null ? Number(rawAmount) : NaN;
			const amount = Number.isFinite(parsedAmount) && parsedAmount > 0 ? parsedAmount : undefined;

			// Resolve all recipients in parallel; drop any that fail to resolve.
			const resolvedRecipients = (await Promise.all(usernames.map(resolveRecipientByUsername))).filter(
				(recipient): recipient is Recipient => recipient !== undefined
			);

			dispatch(GiveBoxEvent.Open, {
				...(resolvedRecipients.length > 0 ? { recipients: resolvedRecipients } : {}),
				...(messageText ? { text: messageText } : {}),
				...(amount != null ? { amount } : {}),
				...(hashtags.length > 0 ? { hashtags } : {}),
				aiGenerated: true,
			});
		};

		// Existing behavior: optional single user pre-selected by id, plus text.
		const dispatchLegacyDeepLink = async () => {
			const userParam: GiveRecognitionParam = params.get(GIVE_RECOGNITION_MODAL_QUERY_PARAM);
			const textParam = params.get(GIVE_RECOGNITION_TEXT_QUERY_PARAM);

			// If no user param
			if (!userParam) {
				if (textParam) {
					dispatch(GiveBoxEvent.Open, { text: textParam });
				}
				return;
			}

			try {
				const directUserResponse = await http.get(`/user_management/users/${userParam}`);

				if (directUserResponse.data) {
					const recipientData = {
						id: directUserResponse.data.id,
						display_name: directUserResponse.data.display_name,
						username: directUserResponse.data.username,
						profile_pic_url: directUserResponse.data.profile_pic_url,
						user_mode: 'user',
					};

					const recipientEntity = new Recipient(recipientData);

					dispatch(GiveBoxEvent.Open, {
						recipient: recipientEntity,
						text: textParam || undefined,
					});
				} else {
					dispatch(GiveBoxEvent.Open, textParam ? { text: textParam } : undefined);
				}
			} catch (directLookupError) {
				try {
					const httpV1 = await getHttpClient('v1');
					const autocompleteResponse = await httpV1.get('/users/autocomplete', {
						params: { search: userParam },
					});

					const matchingRecipients = autocompleteResponse.data.result as RecipientAPIData[];
					const matchingRecipient = matchingRecipients.find((candidate) => candidate.id === userParam);

					if (matchingRecipient) {
						const recipientEntity = new Recipient(matchingRecipient);

						dispatch(GiveBoxEvent.Open, {
							recipient: recipientEntity,
							text: textParam || undefined,
						});
					} else {
						dispatch(GiveBoxEvent.Open, textParam ? { text: textParam } : undefined);
					}
				} catch (autocompleteError) {
					console.error('Error fetching user for give recognition modal:', autocompleteError);
					dispatch(GiveBoxEvent.Open, textParam ? { text: textParam } : undefined);
				}
			}
		};

		initModal();
	}, [dispatch, http, params]);
};
